import { Link } from "react-router-dom";
import { useState } from "react";

interface FooterProps {
  formUrl: string;
}

const quickLinks = [
  { label: "最新动漫", href: "/category/japanese-anime" },
  { label: "热门排行", href: "/category/action-adventure" },
  { label: "电影专区", href: "/category/movies" },
  { label: "国产动漫", href: "/category/chinese-anime" },
  { label: "电视剧", href: "/category/tv-series" },
];

const socialLinks = [
  { label: "微博", icon: "ri-weibo-line", href: "#" },
  { label: "B站", icon: "ri-bilibili-line", href: "#" },
  { label: "微信公众号", icon: "ri-wechat-line", href: "#" },
];

export default function Footer({ formUrl }: FooterProps) {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const form = e.currentTarget as HTMLFormElement;
    const formData = new FormData(form);
    const honeypot = (formData.get("website_alt") as string || "").trim();

    if (honeypot) {
      setSubmitted(true);
      setEmail("");
      return;
    }

    try {
      const res = await fetch(formUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ email }).toString(),
      });
      const responseText = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch {}
      if (res.ok && parsed?.code === "OK") {
        setSubmitted(true);
        setEmail("");
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
        if (serverMsg.includes("spam")) {
          setSubmitted(true);
        } else {
          setError(serverMsg || "订阅失败，请稍后重试");
        }
      }
    } catch (_err) {
      setError("网络错误，请检查连接后重试");
    }
  };

  return (
    <footer className="bg-background-950 text-foreground-100">
      <div className="w-full px-4 md:px-8 py-14 md:py-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-16 mb-16">
            <div>
              <div className="flex items-center gap-2 mb-5">
                <span className="font-heading text-xl font-bold text-white tracking-wide">
                  樱花动漫
                </span>
                <span className="w-1.5 h-1.5 rounded-full bg-primary-500"></span>
              </div>
              <p className="text-sm text-foreground-400 leading-relaxed">
                免费高清动漫在线观看平台。
                <br />
                海量动漫、电视剧、电影资源，
                <br />
                一站式满足您的观影需求。
              </p>
              <p className="text-xs text-foreground-500 mt-4">
                &copy; {new Date().getFullYear()} 樱花动漫网 版权所有
              </p>
            </div>

            <div>
              <h4 className="text-xs font-semibold uppercase tracking-widest text-foreground-300 mb-5 font-label">
                订阅更新
              </h4>

              {submitted ? (
                <p className="text-sm text-accent-400 mt-2">
                  <i className="ri-check-line mr-1"></i>订阅成功！感谢关注
                </p>
              ) : (
                <form onSubmit={handleSubscribe}>
                  <div className="relative">
                    <input
                      type="email"
                      name="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="输入邮箱地址"
                      required
                      className="w-full bg-transparent border-b border-foreground-500/40 py-2.5 pr-10 text-sm text-foreground-100 placeholder-foreground-500 focus:outline-none focus:border-primary-400 transition-colors"
                    />
                    <button
                      type="submit"
                      className="absolute right-0 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-foreground-400 hover:text-primary-400 transition-colors cursor-pointer"
                      aria-label="订阅"
                    >
                      <i className="ri-arrow-right-line"></i>
                    </button>
                  </div>
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                  {error && (
                    <p className="text-xs text-red-400 mt-2">{error}</p>
                  )}
                  <p className="text-xs text-foreground-500 mt-2">
                    订阅后可收到最新动漫更新通知
                  </p>
                </form>
              )}
            </div>

            <div>
              <h4 className="text-xs font-semibold uppercase tracking-widest text-foreground-300 mb-5 font-label">
                快速导航
              </h4>
              <ul className="flex flex-col gap-2.5">
                {quickLinks.map((link) => (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      className="text-sm text-foreground-400 hover:text-white transition-colors duration-200"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link
                    to="/about"
                    className="text-sm text-foreground-400 hover:text-white transition-colors duration-200"
                  >
                    关于我们
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs font-semibold uppercase tracking-widest text-foreground-300 mb-5 font-label">
                关注我们
              </h4>
              <ul className="flex flex-col gap-2.5">
                {socialLinks.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      className="text-sm text-foreground-400 hover:text-white transition-colors duration-200 flex items-center gap-2"
                      rel="nofollow"
                    >
                      <i className={`${link.icon} text-base`}></i>
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="pt-10 border-t border-foreground-800/40">
            <div className="text-center">
              <span className="font-heading text-5xl md:text-7xl font-black tracking-tighter text-foreground-700/30 select-none">
                SAKURA
              </span>
            </div>
            <p className="text-center text-xs text-foreground-600 mt-4">
              本站资源来源于网络，仅供交流学习之用。如侵犯您的权益，请联系我们删除。
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}